import { FC } from "react";
import * as _ from "lodash";

import { Button, Icon, Barn, AntDesignSelect, AntDesignInput, Grid } from "@ctra/components";
import { Enterprise, useTranslation } from "@ctra/i18n";
import { useGoogleAnalytics } from "@ctra/analytics";
import { FarmEntity, SiteEntity } from "@ctra/api";
import { Nullable } from "@ctra/utils";

import { useFarm, useFarmList } from "../../providers";
import { GAActions, GALabels } from "../../analytics";

import styles from "./FarmSelector.module.less";

const { Group } = AntDesignInput;
const { useBreakpoint } = Grid;

/**
 * Type guards to check whether the ID belongs to a farm or site
 * @param {Nullable<number | string>} id
 * @returns {id is SiteEntity["id"]}
 */
const isSiteID = (id: Nullable<FarmEntity["id"] | SiteEntity["id"]>): id is SiteEntity["id"] => {
  return id ? _.isString(id) : false;
};

/**
 * Farm selector component for navigation bar in header
 * @return {JSX.Element}
 * @constructor
 */
export const FarmSelector: FC = ({ children }) => {
  const { t } = useTranslation();
  const { trackEvent } = useGoogleAnalytics();
  const { farmList, siteList, isLoading } = useFarmList();
  const { md, lg } = useBreakpoint();

  const {
    farm,
    site,
    api: { setFarmContext, getFarmName, getSiteName }
  } = useFarm();

  return (
    <Group compact className={styles.InputGroup}>
      {lg && (
        <Button
          size={md ? "middle" : "large"}
          className={styles.Icon}
          icon={<Icon component={Barn} />}
          disabled
        />
      )}
      <AntDesignSelect
        showSearch
        className={styles.Select}
        disabled={_.isEmpty(farmList) || isLoading}
        loading={isLoading}
        labelInValue={true}
        value={{
          value: site ? `${site.id}` : farm ? farm.id : null,
          label: site
            ? getSiteName(site)
            : farm
            ? getFarmName(farm)
            : t<string>(Enterprise.navigation.main.allFarms)
        }}
        showArrow={true}
        size={md ? "middle" : "large"}
        filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
        options={[
          {
            label: t<string>(Enterprise.navigation.main.allFarms),
            value: null
          },
          ..._.map(_.sortBy(farmList, "name"), (farm) =>
            _.size(farm.sites) > 1
              ? {
                  label: getFarmName(farm),
                  options: [
                    {
                      label: t<string>(Enterprise.navigation.main.allSites),
                      value: farm.id
                    },
                    ..._.map(farm.sites, (siteID) => ({
                      label: getSiteName(siteList[siteID]),
                      value: siteID
                    }))
                  ]
                }
              : {
                  label: getFarmName(farm),
                  value: farm.id
                }
          )
        ]}
        onSelect={({
          value: id
        }: {
          value: Nullable<FarmEntity["id"] | SiteEntity["id"]>;
          label: string;
        }) => {
          const farmID = isSiteID(id) ? +id.slice(0, -1) : id;
          const siteID = isSiteID(id) ? id : void 0;

          setFarmContext(farmID, siteID);

          if (farmID) {
            const farm = _.find(farmList, ["id", farmID]);

            if (farm) {
              trackEvent(GAActions.selectFarm, {
                label: getFarmName(farm)
              });
            } else {
              console.error(`Farm with ID ${farmID} not found`);
            }
          } else {
            trackEvent(GAActions.selectFarm, {
              label: GALabels.allFarms
            });
          }
        }}
      />
      {children}
    </Group>
  );
};
